import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { isEnabledLanguage } from '../i18n/languages';
import { API_CONFIG, isProduction } from '../config/api.config';
import { isAiQuotaRequest, notifyAiQuotaChange } from '../utils/aiQuotaEvents';
import { isAiDailyQuotaError } from '../utils/aiErrors';
import { trackEvent } from '../utils/analytics';

// ─── Token Manager ──────────────────────────────────────────────
// Centralized token storage — single source of truth for all services.

export const tokenManager = {
  getToken: (): string | null =>
    localStorage.getItem(API_CONFIG.TOKEN_KEYS.ACCESS_TOKEN),
  setToken: (token: string): void =>
    localStorage.setItem(API_CONFIG.TOKEN_KEYS.ACCESS_TOKEN, token),
  removeToken: (): void =>
    localStorage.removeItem(API_CONFIG.TOKEN_KEYS.ACCESS_TOKEN),

  getRefreshToken: (): string | null =>
    localStorage.getItem(API_CONFIG.TOKEN_KEYS.REFRESH_TOKEN),
  setRefreshToken: (token: string): void =>
    localStorage.setItem(API_CONFIG.TOKEN_KEYS.REFRESH_TOKEN, token),
  removeRefreshToken: (): void =>
    localStorage.removeItem(API_CONFIG.TOKEN_KEYS.REFRESH_TOKEN),

  clearTokens: (): void => {
    localStorage.removeItem(API_CONFIG.TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(API_CONFIG.TOKEN_KEYS.REFRESH_TOKEN);
  },
};

// ─── Shared refresh-queue (singleton across all instances) ──────
// All axios instances share the same queue so that only ONE refresh
// request is in-flight at a given time, regardless of which service
// triggers it.

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// ─── Public helper ──────────────────────────────────────────────

/** Accept-Language matching the UI language (empty if storage is blocked or unset). */
export const languageHeader = (): Record<string, string> => {
  try {
    const selectedLanguage = localStorage.getItem('language');
    return isEnabledLanguage(selectedLanguage) ? { 'Accept-Language': selectedLanguage } : {};
  } catch {
    return {};
  }
};

export interface CreateApiOptions {
  /** Override the default timeout (ms). */
  timeout?: number;
  /** Endpoints that should never trigger a token refresh (e.g. login). */
  skipRefreshFor?: string[];
}

/**
 * Create an axios instance **with** token-auth + auto-refresh interceptors.
 *
 * Every service that needs authenticated requests should call this once
 * instead of manually duplicating the interceptor logic.
 */
export function createAuthenticatedApi(options: CreateApiOptions = {}): AxiosInstance {
  const { timeout = API_CONFIG.TIMEOUTS.DEFAULT, skipRefreshFor = [] } = options;

  const instance = axios.create({
    baseURL: API_CONFIG.BASE_URL,
    timeout,
    headers: API_CONFIG.HEADERS,
  });

  // ── Request interceptor: attach access token ──────────────────
  instance.interceptors.request.use((config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Keep backend locale selection aligned with app language preference.
    Object.assign(config.headers, languageHeader());

    return config;
  });

  // ── Response interceptor: AI calls may have used the daily quota (Faz 10) ──
  instance.interceptors.response.use(
    (response) => {
      if (isAiQuotaRequest(response.config?.method, response.config?.url)) notifyAiQuotaChange();
      return response;
    },
    (error) => {
      if (isAiQuotaRequest(error?.config?.method, error?.config?.url)) notifyAiQuotaChange();
      if (isAiDailyQuotaError(error)) trackEvent('ai_quota_exhausted', { pool: error.response.data.details.quota });
      return Promise.reject(error);
    }
  );

  // ── Response interceptor: 401 → refresh + retry ──────────────
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      // Server-side failures: keep the id that finds this request in the backend logs
      const requestId: string | undefined = error.response?.headers?.['x-request-id'];
      if (requestId) {
        error.requestId = requestId;
        // Production: the backend already logs (and alerts on) its 5xx; callers report with this id
        if (error.response.status >= 500 && !isProduction) {
          console.error(`API ${error.response.status} ${originalRequest?.url ?? ''} (request id: ${requestId})`);
        }
      }

      // Skip refresh for explicitly excluded endpoints
      const url = originalRequest?.url ?? '';
      if (skipRefreshFor.some((pattern) => url.includes(pattern))) {
        return Promise.reject(error);
      }

      // Don't retry refresh-token requests themselves
      if (url.includes('/refresh')) {
        tokenManager.clearTokens();
        window.location.href = '/#/login';
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        // Already refreshing → queue this request
        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = tokenManager.getRefreshToken();
        if (refreshToken) {
          try {
            // rotate: the server replaces the refresh token too (an idle session ends after 30 days)
            const response = await axios.post(
              `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REFRESH}`,
              { rotate: true },
              { headers: { Authorization: `Bearer ${refreshToken}`, ...languageHeader() } },
            );

            const { access_token, refresh_token } = response.data;
            tokenManager.setToken(access_token);
            if (refresh_token) tokenManager.setRefreshToken(refresh_token);

            processQueue(null, access_token);

            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return instance(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            tokenManager.clearTokens();
            window.location.href = '/#/login';
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        } else {
          isRefreshing = false;
          tokenManager.clearTokens();
          window.location.href = '/#/login';
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
}
